/** @jsxImportSource @emotion/react */

import { FieldArrayRenderProps } from 'react-final-form-arrays'
import { FC } from 'react'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import { ReactSortable } from 'react-sortablejs'
import DropzoneField, { ImageField } from 'components/DropzoneField'
import { validateRequired } from 'utils/validation'
import { SORTABLE_LIST_ANIMATION } from 'constants/consts'
import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg'
import { ReactComponent as DnDIcon } from 'assets/icons/dnd-upd.svg'
import { ReactComponent as CrossIcon } from 'assets/icons/cross.svg'
import * as classes from './styles'

const DROPZONE_OPTIONS = {
  accept: {
    'image/*': [],
  },
}

const EMPTY_FIELD_FILTERED_CLASS_NAME = 'screenshot-filtered-empty'

type Item = ImageField[]

type Props = {
  fields: FieldArrayRenderProps<Item, HTMLDivElement>['fields']
  meta: FieldArrayRenderProps<Item, HTMLElement>['meta']
}

export const ScreenshotFieldArray: FC<Props> = ({ fields }) => {
  const sortedFields = (fields.value ?? []).map((value, index) => ({
    id: String(index),
    value,
  }))

  const handleAdd = () => {
    fields.push([])
  }

  const handleDelete = (index: number) => {
    fields.remove(index)
  }

  const handleSort = (newFieldArray: Array<{ id: string; chosen?: boolean; value: Item }>) => {
    const chosenIndex = newFieldArray.findIndex(({ chosen }) => chosen !== undefined)

    if (chosenIndex !== -1) {
      const chosenId = newFieldArray[chosenIndex].id
      const previousIndex = sortedFields.findIndex(({ id }) => id === chosenId)

      if (previousIndex !== -1 && previousIndex !== chosenIndex) {
        fields.move(previousIndex, chosenIndex)
      }
    }
  }

  return (
    <>
      <ReactSortable
        list={sortedFields}
        setList={handleSort}
        css={classes.grid}
        animation={SORTABLE_LIST_ANIMATION}
        filter={`.${EMPTY_FIELD_FILTERED_CLASS_NAME}`}
      >
        {fields.map((field, index) => (
          <div key={index} css={classes.screenshotField}>
            <IconButton css={classes.deleteIconButton} onClick={() => handleDelete(index)}>
              <CrossIcon />
            </IconButton>
            <DnDIcon css={classes.dndIcon} />
            <DropzoneField
              name={field}
              validate={validateRequired}
              dropzoneOptions={DROPZONE_OPTIONS}
              css={classes.dropzoneField}
              renderImage={(file, i, onRemoveClick) => (
                <li key={i} css={classes.listItem}>
                  <div>
                    <img src={file.src} alt="Attached media" css={classes.attachedImage} />
                  </div>
                  <Button
                    variant="outlined"
                    color="red"
                    size="large"
                    onClick={onRemoveClick(i)}
                    css={classes.deleteAttachmentButton}
                  >
                    <TrashIcon />
                    Delete
                  </Button>
                </li>
              )}
              options={{
                maxFiles: 1,
                sliceFromEnd: true,
              }}
            />
          </div>
        ))}
      </ReactSortable>
      <Button variant="outlined" color="blue" size="large" onClick={handleAdd}>
        Add More <AddRoundedIcon />
      </Button>
    </>
  )
}
